import React from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { getStoredUiLang } from '../lib/uiLocale';
import { translateKey } from '../i18n/messages';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Check if error is from MetaMask or other browser extensions
    // Синхронизировано с index.js согласно ТЗ
    const isExtensionError = (err) => {
      if (!err) return false;
      const errorMessage = err.message || err.toString() || '';
      const errorStack = err.stack || '';
      const errorString = String(err);
      
      // Объединяем все строки для проверки
      const combinedText = `${errorMessage} ${errorStack} ${errorString}`.toLowerCase();
      
      const extensionPatterns = [
        // Основные Web3-кошельки
        /metamask/i,
        /phantom/i,
        /solana/i,
        /ethereum/i,
        /web3/i,
        /wallet/i,
        
        // Расширенные названия кошельков (согласно ТЗ)
        /keplr/i,
        /petra/i,
        /suiet/i,
        /aptos/i,
        /sui/i,
        /station/i,
        /slush/i,
        
        // Расширения браузера
        /chrome-extension:/i,
        /moz-extension:/i,
        /safari-extension:/i,
        
        // Сообщения об ошибках подключения
        /failed to connect/i,
        /failed to connect to metamask/i, // Точное сообщение согласно ТЗ
        /could not establish connection/i,
        /receiving end does not exist/i,
        /runtime\.lastError/i,
        /cannot assign to read only property/i,
        /read only property/i,
        
        // Файлы расширений
        /contentScript\.js/i,
        /contentscript\.js/i, // Альтернативное написание
        /inpage\.js/i,
        /solanaActionsContentScript\.js/i,
        /evmAsk\.js/i,
        
        // Паттерны из стека вызовов
        /at Object\.connect/i, // Согласно ТЗ: "at Object.connect" из стека вызова
        /Object\.connect/i,
        /\.connect\s*\(/i, // Любые вызовы метода connect
        
        // Другие паттерны
        /cannot redefine property/i,
      ];
      
      return extensionPatterns.some(pattern => 
        pattern.test(errorMessage) || 
        pattern.test(errorStack) ||
        pattern.test(errorString) ||
        pattern.test(combinedText)
      );
    };

    // Ignore MetaMask and extension errors - don't show error UI
    if (isExtensionError(error)) {
      return null; // Return null to prevent error state update
    }

    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error, errorInfo) {
    // Проверяем, не является ли ошибка от расширения
    const isExtensionError = (err) => {
      if (!err) return false;
      const errorMessage = err.message || err.toString() || '';
      const errorStack = err.stack || '';
      const errorString = String(err);
      const combinedText = `${errorMessage} ${errorStack} ${errorString}`.toLowerCase();
      
      const extensionPatterns = [
        /metamask/i, /phantom/i, /solana/i, /ethereum/i, /web3/i, /wallet/i,
        /keplr/i, /petra/i, /suiet/i, /aptos/i, /sui/i, /station/i, /slush/i,
        /chrome-extension:/i, /moz-extension:/i, /safari-extension:/i,
        /failed to connect/i, /failed to connect to metamask/i,
        /could not establish connection/i, /receiving end does not exist/i,
        /runtime\.lastError/i, /contentScript\.js/i, /contentscript\.js/i,
        /inpage\.js/i, /at Object\.connect/i, /Object\.connect/i, /\.connect\s*\(/i
      ];
      
      return extensionPatterns.some(pattern => 
        pattern.test(errorMessage) || pattern.test(errorStack) ||
        pattern.test(errorString) || pattern.test(combinedText)
      );
    };
    
    // Если ошибка от расширения, не логируем и не отправляем в системы мониторинга
    if (isExtensionError(error)) {
      return; // Молча игнорируем
    }
    
    // Log error details только для реальных ошибок приложения
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
      retryCount: this.state.retryCount + 1
    });

    // Report error to monitoring service только для реальных ошибок
    this.reportError(error, errorInfo);
  }

  reportError = (error, errorInfo) => {
    // Согласно ТЗ: ошибки от Web3-расширений не должны попадать в системы логирования
    // Проверка уже выполнена в componentDidCatch, но добавляем дополнительную защиту
    const isExtensionError = (err) => {
      if (!err) return false;
      const errorMessage = err.message || err.toString() || '';
      const errorStack = err.stack || '';
      const combinedText = `${errorMessage} ${errorStack}`.toLowerCase();
      
      const extensionPatterns = [
        /metamask/i, /phantom/i, /solana/i, /ethereum/i, /web3/i, /wallet/i,
        /keplr/i, /petra/i, /suiet/i, /aptos/i, /sui/i,
        /chrome-extension:/i, /moz-extension:/i, /safari-extension:/i,
        /failed to connect/i, /at Object\.connect/i
      ];
      
      return extensionPatterns.some(pattern => 
        pattern.test(errorMessage) || pattern.test(errorStack) || pattern.test(combinedText)
      );
    };
    
    // Если ошибка от расширения, не отправляем в системы мониторинга
    if (isExtensionError(error)) {
      return; // Согласно ТЗ: не отправляем ошибки расширений в логирование
    }
    
    const errorData = {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      retryCount: this.state.retryCount,
      props: this.props
    };

    // In a real application, send to error monitoring service (Sentry, Bugsnag и т.д.)
    console.error('Error reported:', errorData);

    // Trigger global error handler if available
    // Согласно ТЗ: только реальные ошибки приложения попадают в системы мониторинга
    if (window.errorReportingHandler) {
      window.errorReportingHandler(errorData);
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      const loc = getStoredUiLang();
      const t = (key, vars) => translateKey(loc, key, vars);

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                {t('errorBoundary.title')}
              </CardTitle>
              <CardDescription className="text-gray-600">
                {t('errorBoundary.subtitle')}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Error Details (only in development) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Alert className="border-red-200 bg-red-50">
                  <Bug className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    <details className="mt-2">
                      <summary className="cursor-pointer font-medium">
                        {t('errorBoundary.devDetails')}
                      </summary>
                      <div className="mt-2 text-sm">
                        <p><strong>{t('errorBoundary.errorLabel')}</strong> {this.state.error.message}</p>
                        <p><strong>{t('errorBoundary.idLabel')}</strong> {this.state.errorId}</p>
                        {this.state.error.stack && (
                          <pre className="mt-2 whitespace-pre-wrap text-xs bg-red-100 p-2 rounded">
                            {this.state.error.stack}
                          </pre>
                        )}
                      </div>
                    </details>
                  </AlertDescription>
                </Alert>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={this.handleRetry}
                  className="flex items-center gap-2"
                  variant="default"
                >
                  <RefreshCw className="h-4 w-4" />
                  {t('errorBoundary.retry')}
                </Button>
                
                <Button
                  onClick={this.handleReload}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  {t('errorBoundary.reload')}
                </Button>
                
                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Home className="h-4 w-4" />
                  {t('errorBoundary.home')}
                </Button>
              </div>

              {/* Retry Count */}
              {this.state.retryCount > 1 && (
                <div className="text-center text-sm text-gray-500">
                  {t('errorBoundary.retries', { n: this.state.retryCount })}
                </div>
              )}

              {/* Help Text */}
              <div className="text-center text-sm text-gray-500">
                {t('errorBoundary.help')}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook for error boundary functionality
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error) => {
    setError(error);
  }, []);

  return {
    error,
    resetError,
    captureError
  };
};

/**
 * Higher-order component for error boundary
 */
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

/**
 * Global error handler setup
 */
export const setupGlobalErrorHandler = () => {
  // Check if error is from MetaMask or other browser extensions
  // Синхронизировано с index.js согласно ТЗ
  const isExtensionError = (error) => {
    if (!error) return false;
    const errorMessage = error.message || error.toString() || '';
    const errorStack = error.stack || '';
    const errorString = String(error);
    
    // Объединяем все строки для проверки
    const combinedText = `${errorMessage} ${errorStack} ${errorString}`.toLowerCase();
    
    // Filter out MetaMask and other extension errors
    const extensionPatterns = [
      // Основные Web3-кошельки
      /metamask/i,
      /phantom/i,
      /solana/i,
      /ethereum/i,
      /web3/i,
      /wallet/i,
      
      // Расширенные названия кошельков (согласно ТЗ)
      /keplr/i,
      /petra/i,
      /suiet/i,
      /aptos/i,
      /sui/i,
      /station/i,
      /slush/i,
      
      // Расширения браузера
      /chrome-extension:/i,
      /moz-extension:/i,
      /safari-extension:/i,
      
      // Сообщения об ошибках подключения
      /failed to connect/i,
      /failed to connect to metamask/i, // Точное сообщение согласно ТЗ
      /could not establish connection/i,
      /receiving end does not exist/i,
      /runtime\.lastError/i,
      
      // Файлы расширений
      /contentScript\.js/i,
      /contentscript\.js/i, // Альтернативное написание
      /inpage\.js/i,
      /solanaActionsContentScript\.js/i,
      /evmAsk\.js/i,
      
      // Паттерны из стека вызовов
      /at Object\.connect/i, // Согласно ТЗ: "at Object.connect" из стека вызова
      /Object\.connect/i,
      /\.connect\s*\(/i, // Любые вызовы метода connect
      
      // Другие паттерны
      /cannot redefine property/i,
    ];
    
    return extensionPatterns.some(pattern => 
      pattern.test(errorMessage) || 
      pattern.test(errorStack) ||
      pattern.test(errorString) ||
      pattern.test(combinedText)
    );
  };

  // Handle unhandled promise rejections
  // Согласно ТЗ: принудительное прерывание с stopImmediatePropagation
  window.addEventListener('unhandledrejection', (event) => {
    // Ignore MetaMask and extension errors
    if (isExtensionError(event.reason)) {
      // Согласно ТЗ: принудительное прерывание обработки
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }
      return false; // Согласно ТЗ: вернуть false для прерывания
    }
    
    console.error('Unhandled promise rejection:', event.reason);
    
    if (window.errorReportingHandler) {
      window.errorReportingHandler({
        type: 'unhandledRejection',
        error: event.reason,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    }
  }, true); // Use capture phase to catch early

  // Handle global JavaScript errors
  // Согласно ТЗ: принудительное прерывание с stopImmediatePropagation
  window.addEventListener('error', (event) => {
    const isFromExtension = isExtensionError(event.error) || 
        (event.filename && /chrome-extension:|moz-extension:|safari-extension:/i.test(event.filename)) ||
        (event.message && isExtensionError({ message: event.message }));
    
    // Ignore MetaMask and extension errors
    if (isFromExtension) {
      // Согласно ТЗ: принудительное прерывание обработки
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }
      return false; // Согласно ТЗ: вернуть false для прерывания
    }
    
    console.error('Global error:', event.error);
    
    if (window.errorReportingHandler) {
      window.errorReportingHandler({
        type: 'globalError',
        error: event.error,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    }
  }, true); // Use capture phase to catch early
};

// Initialize global error handler
if (typeof window !== 'undefined') {
  setupGlobalErrorHandler();
  
  // Note: Wallet blocking is handled in index.js before React initializes
  // This ensures extensions are blocked before any component code runs
}

export default ErrorBoundary;
